import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EquipoAsignado } from 'src/app/shared/models/entrega-equipo-link.interface';
import { EntregaEquipoLinkService } from 'src/app/pages/services/entrega-equipo-link.service';
import { EditarMovimientoDialogComponent } from '../editar-movimiento-dialog/editar-movimiento-dialog.component';
import { ReportarIncidenteDialogComponent } from '../reportar-incidente-dialog/reportar-incidente-dialog.component';
import { VincularTrabajadorDialogComponent } from '../vincular-trabajador-dialog/vincular-trabajador-dialog.component';
import { EntregaEquipoPdfService } from 'src/app/pages/services/PDF-GENERADOR/entrega-equipo/entrega-equipo-pdf.service';

interface GrupoTrabajadorAsignado {
  trabajadorNombre: string;
  rut: string | null;
  fk_trabajador: number | null;
  equipos: EquipoAsignado[];
  verHistorial: boolean;
  cargandoHistorial: boolean;
  historial: any[];
  pendienteVencido: boolean;
  finiquitado: boolean;
}

// Un "sin vincular" que lleva más de esto sin que nadie lo reconcilie con un trabajador
// real, se marca como vencido/para revisar (solo visual, no cambia nada en la BD).
const DIAS_LIMITE_SIN_VINCULAR = 30;

@Component({
  selector: 'app-listar-asignados-entrega-equipos',
  templateUrl: './listar-asignados-entrega-equipos.component.html',
  styleUrls: ['./listar-asignados-entrega-equipos.component.css']
})
export class ListarAsignadosEntregaEquiposComponent implements OnChanges {

  @Input() asignados_List!: EquipoAsignado[];
  @Output() actualizado: EventEmitter<boolean> = new EventEmitter();

  grupos: GrupoTrabajadorAsignado[] = [];

  constructor(
    private entregaEquipoLinkService: EntregaEquipoLinkService,
    private dialog: MatDialog,
    private entregaEquipoPdfService: EntregaEquipoPdfService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.asignados_List != undefined) {
      this.grupos = this.agruparPorTrabajador(this.asignados_List || []);
    }
  }

  private agruparPorTrabajador(lista: EquipoAsignado[]): GrupoTrabajadorAsignado[] {
    const mapa = new Map<string, GrupoTrabajadorAsignado>();

    lista.forEach((item) => {
      const rut = item.TrabajadorAsignado?.rut ?? null;
      const nombre = item.TrabajadorAsignado
        ? `${item.TrabajadorAsignado.nombres} ${item.TrabajadorAsignado.apellidos}`
        : 'Sin vincular';
      // fk_trabajador es null para TODOS los movimientos "sin vincular" (aún no sabemos a
      // qué trabajador real corresponden), así que agrupar por ese valor mezclaría entregas
      // de personas distintas en un solo grupo. Se usa el id de la fila para que cada una
      // quede separada hasta que se vincule.
      const clave = rut !== null ? String(rut) : `sin-vincular-${item.id}`;

      if (!mapa.has(clave)) {
        mapa.set(clave, {
          trabajadorNombre: nombre,
          rut,
          fk_trabajador: item.fk_trabajador,
          equipos: [],
          verHistorial: false,
          cargandoHistorial: false,
          historial: [],
          pendienteVencido: false,
          finiquitado: item.TrabajadorAsignado?.estado_contrato === 'Finiquitado'
        });
      }
      mapa.get(clave)!.equipos.push(item);
    });

    const grupos = Array.from(mapa.values());
    grupos.forEach((grupo) => {
      if (grupo.rut === null) {
        const masAntigua = grupo.equipos.reduce((min, eq) => {
          const fecha = new Date(eq.fechaMovimiento).getTime();
          return fecha < min ? fecha : min;
        }, Infinity);
        const diasTranscurridos = (Date.now() - masAntigua) / (1000 * 60 * 60 * 24);
        grupo.pendienteVencido = diasTranscurridos > DIAS_LIMITE_SIN_VINCULAR;
      }
    });

    return grupos.sort((a, b) => a.trabajadorNombre.localeCompare(b.trabajadorNombre));
  }

  toggleHistorial(grupo: GrupoTrabajadorAsignado) {
    grupo.verHistorial = !grupo.verHistorial;
    if (grupo.verHistorial && grupo.historial.length === 0 && grupo.fk_trabajador) {
      grupo.cargandoHistorial = true;
      this.entregaEquipoLinkService.listarHistorialPorTrabajador(grupo.fk_trabajador).subscribe(res => {
        grupo.cargandoHistorial = false;
        grupo.historial = res.data || [];
      });
    }
  }

  verPdf(movimiento: any) {
    this.entregaEquipoLinkService.buscarPorMovimiento(movimiento.id).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.entregaEquipoPdfService.generarPdf(res.data);
        }
      },
      error: () => { /* el error ya se muestra vía handlerError del service */ }
    });
  }

  abrirReportarIncidente(movimiento: any) {
    const dialogRef = this.dialog.open(ReportarIncidenteDialogComponent, {
      width: '450px',
      data: { movimiento }
    });
    dialogRef.afterClosed().subscribe((confirmado) => {
      if (confirmado) {
        this.actualizado.emit(true);
      }
    });
  }

  abrirVincularTrabajador(movimiento: any) {
    const dialogRef = this.dialog.open(VincularTrabajadorDialogComponent, {
      width: '450px',
      data: { movimiento }
    });
    dialogRef.afterClosed().subscribe((vinculado) => {
      if (vinculado) {
        this.actualizado.emit(true);
      }
    });
  }

  abrirEditarMovimiento(movimiento: any) {
    const dialogRef = this.dialog.open(EditarMovimientoDialogComponent, {
      width: '500px',
      data: { movimiento }
    });
    dialogRef.afterClosed().subscribe((cambios) => {
      if (cambios) {
        this.actualizado.emit(true);
      }
    });
  }

}
